import {useCallback, useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {GridItem, useDisclosure} from '@chakra-ui/react'
import {useLocation} from 'react-router'
import {useTranslation} from 'react-i18next'
import useSWRMutation from 'swr/mutation'
import {useRails} from '../../contexts/rails'
import {useTokenFreshnessCheck} from '../../hooks/useTokenFreshnessCheck'
import {fetcher, defaultHeaders} from '../../../api/fetcher'
import {refreshStepperData} from '../CashinStepper/useStepperData'
import ExitConfirmationDialog from '../ExitConfirmationDialog'
import StepFlowModal from '../StepFlowModal'
import useStepNavigation, {isLastStep} from './useStepNavigation'
import {
  ConfirmationScreen,
  InformationScreen,
  SelectAccountScreen,
} from './screens'

const ConnectExpenseAccountModals = ({isOpen, onClose}) => {
  const {
    isOpen: isAlertOpen,
    onOpen: onAlertOpen,
    onClose: onAlertClose,
  } = useDisclosure()

  const location = useLocation()
  const {t} = useTranslation('connectExpenseAccount')
  const {
    organisation: {currentSupplier},
    authenticityToken,
  } = useRails()

  // Check and refresh integration token if needed when modal opens
  useTokenFreshnessCheck(isOpen ? currentSupplier?.id : null)

  // Create expense account mutation
  const {trigger: createExpenseAccount, isMutating: isCreatingExpenseAccount} =
    useSWRMutation(
      `/api/suppliers/${currentSupplier?.id}/expense_accounts`,
      async (url, {arg}) =>
        fetcher(url, {
          method: 'POST',
          headers: defaultHeaders({authenticityToken}),
          body: JSON.stringify({
            expense_account: {
              name: arg.name,
              description: arg.description,
            },
          }),
        }),
    )

  const getCurrentStepFromPath = useCallback(() => {
    const pathParts = location.pathname.split('/')
    const lastPart = pathParts[pathParts.length - 1]

    if (lastPart === 'connect_payout_account' || !lastPart) {
      return 'information'
    }

    return lastPart.replace(/-/g, '_')
  }, [location.pathname])

  const [step, setStep] = useState(getCurrentStepFromPath())
  const [createError, setCreateError] = useState(null)

  // Update step when path changes
  useEffect(() => {
    setStep(getCurrentStepFromPath())
  }, [location.pathname, getCurrentStepFromPath])

  const {
    goToNextStep,
    goToPreviousStep,
    goToDashboard,
    goToConfirmation,
    goToNotifyCustomers,
  } = useStepNavigation(step)

  const handleCreateExpenseAccount = async ({
    name = 'expense account',
    description = 'description',
  } = {}) => {
    setCreateError(null)
    try {
      await createExpenseAccount({name, description})
      goToConfirmation()
    } catch {
      setCreateError(t('errors.createFailed'))
    }
  }

  const handleClose = () => {
    if (isLastStep(step)) {
      refreshStepperData()
      onClose()
    } else {
      onAlertOpen()
    }
  }

  const handleConfirmClose = () => {
    onAlertClose()
    goToDashboard()
  }

  const getModalStep = () => {
    switch (step) {
      case 'information':
        return (
          <InformationScreen
            handleClose={handleClose}
            goToNextStep={goToNextStep}
            onCreateExpenseAccount={handleCreateExpenseAccount}
            isCreatingExpenseAccount={isCreatingExpenseAccount}
          />
        )
      case 'select_account':
        return (
          <SelectAccountScreen
            handleClose={handleClose}
            goToNextStep={goToNextStep}
            onCreateExpenseAccount={handleCreateExpenseAccount}
            createError={createError}
          />
        )
      case 'confirmation':
        return <ConfirmationScreen goToNotifyCustomers={goToNotifyCustomers} />
      default:
        return (
          <InformationScreen
            handleClose={handleClose}
            goToNextStep={goToNextStep}
            onCreateExpenseAccount={handleCreateExpenseAccount}
            isCreatingExpenseAccount={isCreatingExpenseAccount}
          />
        )
    }
  }

  const modalStep = getModalStep()

  return (
    <>
      <StepFlowModal
        isOpen={isOpen}
        handleClose={handleClose}
        introductionTitle={t('introduction.title')}
        introductionSubtitle={t('introduction.subtitle')}
        goToPreviousStep={goToPreviousStep}
        goToPreviousStepEnabled={step === 'select_account'}
      >
        <GridItem colSpan={2} position="relative">
          {modalStep}
        </GridItem>
      </StepFlowModal>
      <ExitConfirmationDialog
        isOpen={isAlertOpen}
        onClose={onAlertClose}
        onConfirmExit={handleConfirmClose}
      />
    </>
  )
}

ConnectExpenseAccountModals.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default ConnectExpenseAccountModals
